import { richTextFromMarkdown } from '@contentful/rich-text-from-markdown'
import type { Document } from '@contentful/rich-text-types'

const space = process.env.NEXT_PUBLIC_CONTENTFUL_SPACEID || 'dummy-space-id'
const accessToken = process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN || 'dummy-access-token'

// Only create client if we have real credentials
const client = (space === 'dummy-space-id' || accessToken === 'dummy-access-token')
  ? null
  : require('contentful').createClient({
    space,
    accessToken
  })

export const landingPageBackgroundColor = {
  sand: 'Solid Sand',
  midnightBlue: 'Solid Midnight Blue',
  glow: 'Glow',
  dawn: 'Dawn',
  dusk: 'Dusk'
}
export type BackgroundColorType = 'Solid Sand' | 'Solid Midnight Blue' | 'Glow' | 'Dawn' | 'Dusk' | null
export type CtaColorType = 'Primary Orange' | 'Primary Blue' | 'Secondary Sand' | 'Secondary Orange'
export type CtaType = {
  ctaUrl: string,
  ctaText: string,
  ctaColor: CtaColorType,
  downloadTheAppOverride: boolean,
  downloadTheAppOverrideText: string | null,
  openOnNewTab: boolean,
  useAppStoreBadges: boolean
}
export type PaywallCtaType = {
  ctaText: string,
  ctaColor: CtaColorType,
  authSubheaderText: string | null,
  confirmationHeaderText: string | null,
  confirmationSubheaderText: string | null
}
export type CtaDataType = {
  type: 'paywallCta' | 'cta',
  paywallCta: PaywallCtaType | null,
  cta: CtaType | null
}

export type ContentBlockHorizontalImageAndTextType = {
  id: string,
  type: string,
  backgroundImage: string | null,
  backgroundColor: BackgroundColorType,
  squareImage: string | null, // no longer has to be square
  imageWidth: number | null,
  imageHeight: number | null,
  headerText: string,
  descriptionText: Document | null,
  cta: CtaDataType | null,
  isImageOnBottomAtMobileBreakpoint: boolean,
  imageLink: string | null
}
export type ContentBlockCenteredTextAndImageType = {
  id: string,
  type: string,
  backgroundImage: string | null,
  backgroundColor: BackgroundColorType,
  headerText: string,
  descriptionText: Document | null,
  cta: CtaDataType | null,
  mobileBottomImage: {
    url: string | null,
    height: number | null,
    width: number | null
  },
  tabletBottomImage: {
    url: string | null,
    height: number | null,
    width: number | null
  },
  desktopBottomImage: {
    url: string | null,
    height: number | null,
    width: number | null
  },
  imageLink: string | null
}
export type SpotlightCardsType = {
  id: string,
  headerText: string,
  descriptionText: Document | null,
  squareImage: string,
  cta: CtaDataType | null
}
export type ContentBlockSpotlightSectionType = {
  id: string,
  type: string,
  sectionTitle: string,
  isFirstImageUsedForAll: boolean,
  isDarkTheme: boolean,
  spotlightCards: SpotlightCardsType[]
}
export type ContentBlockUniverseSectionType = {
  id: string,
  type: string,
  headerText: string,
  descriptionText: Document | null,
  cta: CtaDataType | null,
  desktopImage: string | null,
  tabletTopImage: string | null,
  tabletBottomImage: string | null,
  mobileTopImage: string | null,
  mobileBottomImage: string | null,
  backgroundColor: BackgroundColorType
}
export type ContentBlockLongTextType = {
  id: string,
  type: string,
  backgroundColor: BackgroundColorType,
  textColor: 'sand' | 'white' | 'midnightBlue',
  bodyText: Document | null,
  bottomCardBackgroundImage: string | null,
  bottomCardBackgroundColor: BackgroundColorType,
  bottomCardText: Document | null,
  bottomCardCta: CtaDataType | null,
  isFadeOutGradientVisible: boolean
}
export type ContentBlocksType =
  ContentBlockHorizontalImageAndTextType |
  ContentBlockCenteredTextAndImageType |
  ContentBlockSpotlightSectionType |
  ContentBlockUniverseSectionType |
  ContentBlockLongTextType
// NOTE: 'Only Drive To Dowload' is spelled without the second 'n' here, but
// CarouselAndDriveToDownloadSection compares against 'Only Drive To Download'.
// One of the two is wrong - confirm the Contentful field value.
export type BottomCarouselType = 'None' | 'Only Drive To Dowload' | 'Only Drive To Download' | 'Press Quotes Cards' | 'User Quotes Cards' | 'User Quotes Slides'
export type AudioClipType = {
  title: string,
  audioUrl: string,
  backgroundColor: BackgroundColorType,
  cta: CtaDataType | null
}
export type MarketingLandingPageType = {
  id: string,
  slug: string,
  metaTitle: string,
  metaDescription: string,
  contentBlocks: ContentBlocksType[],
  bottomCarousel: BottomCarouselType,
  audioPlayer?: AudioClipType | null,
  navigationShowDefaultTopNav: boolean,
  navigationShowFooterOnPage: boolean,
  navigationSubscribeButtonOpensModal: boolean
}

export type MarektingLandingPageSlugsType = { paths: { params: { slug: string } }[], fallback: boolean }

export async function generateMarketingLandingPageSlugs (): Promise<MarektingLandingPageSlugsType> {
  if (!client) {
    console.warn('Contentful client not available - returning empty marketing landing page slugs')
    return { paths: [], fallback: false }
  }

  const queryParams: any = {
    content_type: 'marketingLandingPage',
    limit: 1000,
    select: 'fields.slug'
  }
  let paths: any[] = []
  try {
    const entries = await client.getEntries(queryParams)
    const slugs: string[] = entries.items.map((item: any): any => item.fields.slug)
    const finalSlugs = slugs.filter((slug: string): boolean => !slug.includes('placeholder'))
    // const finalSlugs = slugs
    paths = finalSlugs.map((slug: string): { params: { slug: string } } => ({ params: { slug } }))
  } catch (e) {
    console.error(e)
  }
  return {
    paths,
    fallback: false
  }
}

function getCtaObject (data: any): CtaDataType | null {
  if (data && data.fields) {
    if (data.sys.contentType.sys.id === 'cta') {
      return {
        type: 'cta',
        paywallCta: null,
        cta: {
          ctaUrl: data.fields.ctaUrl,
          ctaText: data.fields.ctaText,
          ctaColor: data.fields.ctaColor,
          downloadTheAppOverride: data.fields.downloadTheAppOverride,
          downloadTheAppOverrideText: data.fields.downloadTheAppOverrideText || null,
          openOnNewTab: !!data.fields.openOnNewTab,
          useAppStoreBadges: !!data.fields.useAppStoreBadges
        }
      }
    }

    return {
      type: 'paywallCta',
      paywallCta: {
        ctaText: data.fields.ctaText,
        ctaColor: data.fields.ctaColor,
        authSubheaderText: data.fields.authSubheaderText || null,
        confirmationHeaderText: data.fields.confirmationHeaderText || null,
        confirmationSubheaderText: data.fields.confirmationSubheaderText || null
      },
      cta: null
    }
  } else {
    return null
  }
}

async function getCta (data: any): Promise<CtaDataType | null> {
  if (data && data.fields) {
    return getCtaObject(data)
  } else if (data && data.sys && data.sys.id) {
    try {
      const cta = await fetchCta(data.sys.id)
      if (cta) {
        return cta
      }
    } catch (e) {
      return null
    }
  }

  return null
}

export async function fetchCta (id: string): Promise<CtaDataType | null | undefined> {
  if (!client) {
    console.warn('Contentful client not available - returning null for CTA')
    return null
  }

  try {
    // try to fetch cta with id
    const queryParams: any = {
      content_type: 'cta',
      limit: 1,
      'sys.id': id,
      include: 1
    }
    let entries = await client.getEntries(queryParams)

    if (!entries) {
      //  if there isn't a cta entry with this id, try to fetch paywallCta with id
      const queryParams: any = {
        content_type: 'paywallCta',
        limit: 1,
        'sys.id': id,
        include: 1
      }
      entries = await client.getEntries(queryParams)
    }

    const data = entries.items[0]

    if (data) {
      return getCtaObject(data)
    } else {
      return null
    }
  } catch (e) {
    console.error(e)
  }
}

async function getSpotLightCards (data: any): Promise<SpotlightCardsType[] | null | undefined> {
  if (data && data.length > 1) {
    const cards: any[] = []

    await Promise.all(data.map(async (item: any): Promise<void> => {
      const content: SpotlightCardsType = {
        id: item.sys.id,
        headerText: item.fields.headerText,
        descriptionText: await getRichText(item.fields.descriptionText) || null,
        squareImage: item.fields.squareImage?.fields.file.url || null,
        cta: await getCta(item.fields.cta) || null
      }
      cards.push(content)
    }))

    const origialOrderOfIds = data.map((item: any): string => item.sys.id)

    const sortedCards = cards.sort((current: SpotlightCardsType, next: SpotlightCardsType): number => {
      const currentIndex = origialOrderOfIds.indexOf(current.id)
      const nextIndex = origialOrderOfIds.indexOf(next.id)
      if (currentIndex < nextIndex) {
        return -1
      } else if (currentIndex > nextIndex) {
        return 1
      } else {
        return 0
      }
    })

    return sortedCards
  }
  return null
}

async function getRichText (text: string): Promise<Document | null | undefined> {
  if (!text) {
    return null
  }

  try {
    return await richTextFromMarkdown(text)
  } catch (e) {
    console.error(e)
  }
}

async function getContentBlocks (data: []): Promise<ContentBlocksType[] | null | undefined> {
  const contentBlocks: any[] = []

  await Promise.all(data.map(async (item: any): Promise<void> => {
    if (item.sys.contentType.sys.id === 'contentBlockHorizontalImageAndText') {
      const content: ContentBlockHorizontalImageAndTextType = {
        id: item.sys.id,
        type: item.sys.contentType.sys.id,
        backgroundImage: item.fields.backgroundImage?.fields.file.url || null,
        backgroundColor: item.fields.backgroundColor || null,
        squareImage: item.fields.squareImage?.fields.file.url || null, // no longer has to be a square image.
        imageWidth: item.fields.squareImage?.fields.file.details.image.width || null,
        imageHeight: item.fields.squareImage?.fields.file.details.image.height || null,
        headerText: item.fields.headerText || null,
        descriptionText: await getRichText(item.fields.descriptionText) || null,
        cta: await getCta(item.fields.cta) || null,
        isImageOnBottomAtMobileBreakpoint: !!item.fields.isImageOnBottomAtMobileBreakpoint,
        imageLink: item.fields.imageLink || null
      }
      contentBlocks.push(content)
    } else if (item.sys.contentType.sys.id === 'contentBlockCenteredTextAndImage') {
      const content: ContentBlockCenteredTextAndImageType = {
        id: item.sys.id,
        type: item.sys.contentType.sys.id,
        backgroundImage: item.fields.backgroundImage?.fields.file.url || null,
        backgroundColor: item.fields.backgroundColor || null,
        headerText: item.fields.headerText || null,
        descriptionText: await getRichText(item.fields.descriptionText) || null,
        mobileBottomImage: {
          url: item.fields.mobileBottomImage?.fields.file.url || null,
          height: item.fields.mobileBottomImage?.fields.file.details.image.height || null,
          width: item.fields.mobileBottomImage?.fields.file.details.image.width || null
        },
        tabletBottomImage: {
          url: item.fields.tabletBottomImage?.fields.file.url || null,
          height: item.fields.tabletBottomImage?.fields.file.details.image.height || null,
          width: item.fields.tabletBottomImage?.fields.file.details.image.width || null
        },
        desktopBottomImage: {
          url: item.fields.desktopBottomImage?.fields.file.url || null,
          height: item.fields.desktopBottomImage?.fields.file.details.image.height || null,
          width: item.fields.desktopBottomImage?.fields.file.details.image.width || null
        },
        cta: await getCta(item.fields.cta) || null,
        imageLink: item.fields.imageLink || null
      }
      contentBlocks.push(content)
    } else if (item.sys.contentType.sys.id === 'contentBlockSpotlightSection') {
      const content: ContentBlockSpotlightSectionType = {
        id: item.sys.id,
        type: item.sys.contentType.sys.id,
        sectionTitle: item.fields.sectionTitle || null,
        isFirstImageUsedForAll: !!item.fields.isFirstImageUsedForAll,
        isDarkTheme: item.fields.isDarkTheme,
        spotlightCards: await getSpotLightCards(item.fields.spotlightCards) || []
      }
      contentBlocks.push(content)
    } else if (item.sys.contentType.sys.id === 'contentBlockUniverseSection') {
      const content: ContentBlockUniverseSectionType = {
        id: item.sys.id,
        type: item.sys.contentType.sys.id,
        headerText: item.fields.headerText || null,
        descriptionText: await getRichText(item.fields.descriptionText) || null,
        cta: await getCta(item.fields.cta) || null,
        desktopImage: item.fields.desktopImage?.fields.file.url || null,
        tabletTopImage: item.fields.tabletTopImage?.fields.file.url || null,
        tabletBottomImage: item.fields.tabletBottomImage?.fields.file.url || null,
        mobileTopImage: item.fields.mobileTopImage?.fields.file.url || null,
        mobileBottomImage: item.fields.mobileBottomImage?.fields.file.url || null,
        backgroundColor: item.fields.backgroundColor || null
      }
      contentBlocks.push(content)
    } else if (item.sys.contentType.sys.id === 'contentBlockLongText') {
      const content: ContentBlockLongTextType = {
        id: item.sys.id,
        type: item.sys.contentType.sys.id,
        backgroundColor: item.fields.backgroundColor || null,
        textColor: item.fields.textColor || 'white',
        bodyText: item.fields.richText || null,
        bottomCardBackgroundImage: item.fields.bottomCardBackgroundImage?.fields.file.url || null,
        bottomCardBackgroundColor: item.fields.bottomCardBackgroundColor || 'Dusk',
        bottomCardText: item.fields.bottomCardRichText || null,
        bottomCardCta: await getCta(item.fields.bottomCardCta) || null,
        isFadeOutGradientVisible: !!item.fields.isFadeOutGradientVisible
      }

      contentBlocks.push(content)
    }
  }))

  const origialOrderOfIds = data.map((item: any): string => item.sys.id)

  const sortedBlocks = contentBlocks.sort((current: ContentBlocksType, next: ContentBlocksType): number => {
    const currentIndex = origialOrderOfIds.indexOf(current.id)
    const nextIndex = origialOrderOfIds.indexOf(next.id)
    if (currentIndex < nextIndex) {
      return -1
    } else if (currentIndex > nextIndex) {
      return 1
    } else {
      return 0
    }
  })

  return sortedBlocks
}

async function getAudioClip (data: any): Promise<AudioClipType | null | undefined> {
  try {
    if (data && data.fields) {
      return {
        title: data.fields?.audioTitle || null,
        audioUrl: data.fields?.audioFile?.fields?.file?.url || null,
        backgroundColor: data.fields?.backgroundColor || null,
        cta: await getCta(data.fields?.cta) || null
      }
    }
  } catch (e) {
    return null
  }

  return null
}

async function contertItemIntoMarketingLandingPage (item: any): Promise<MarketingLandingPageType | null | undefined> {
  if (!item) {
    return null
  }

  try {
    const landingPage: MarketingLandingPageType = {
      id: item.sys.id,
      slug: item.fields.slug,
      metaTitle: item.fields.metaTitle || null,
      metaDescription: item.fields.metaDescription || null,
      bottomCarousel: item.fields.bottomCarousel || null,
      audioPlayer: await getAudioClip(item.fields.audioPlayer) || null,
      contentBlocks: await getContentBlocks(item.fields.contentBlocks || []) || [],
      navigationShowDefaultTopNav: !!item.fields.navigationShowDefaultTopNav,
      navigationShowFooterOnPage: !!item.fields.navigationShowFooterOnPage,
      navigationSubscribeButtonOpensModal: !!item.fields.navigationSubscribeButtonOpensModal
    }

    return landingPage
  } catch (e) {
    console.error(e)
  }
}

export async function fetchMarketingLandingPage (slug: string): Promise<MarketingLandingPageType | null | undefined> {
  if (!client) {
    console.warn('Contentful client not available - returning null for marketing landing page')
    return null
  }

  const queryParams: any = {
    content_type: 'marketingLandingPage',
    limit: 1,
    'fields.slug': slug,
    include: 2
  }

  try {
    const entries = await client.getEntries(queryParams)
    const item = entries.items[0]
    const landingPage = await contertItemIntoMarketingLandingPage(item)
    return landingPage
  } catch (e) {
    console.error(e)
  }
}
